import { Controller } from 'stimulus';

// The progress of an audio clip block on the post page (Block::Newsletter::ClipComponent#render_live): how far
// into the clip, which is the part of the episode between start and end, the reader's playback is.
//
// It sits on the app's existing playback infrastructure rather than a player of its own. The clip's play
// button is the episode's regular PlayButtonComponent, which starts the one shared audio player at the clip's
// start. That player announces its position on the window as `supercast-timeupdate` events ({ itemId,
// itemType, currentTime, duration, isPlaying }), and playback_database.js stores the last one per episode in
// IndexedDB (window.playbackDb, the same store MediaProgressController reads). So this controller
//   - seeds the bar from window.playbackDb.getPlaybackData(itemType, itemId) when it connects,
//   - then follows `supercast-timeupdate` for the clip's episode,
//   - shows (currentTime - start) / (end - start) clamped to 0..100%, and
//   - when playback was started from this clip's own button, pauses the shared audio player as it reaches
//     the end, the same way PlayButtonController#pause does (the player's own controller, found by its
//     identifier). Playback the reader started any other way, including the episode simply running through
//     the clip, is never stopped: the bar just reaches 100% and stays there.
//
// Native apps play through a bridge component (see PlayButtonController's warning) that may not emit these
// web events; there the bar stays where it is and nothing is paused.

// The player reports every ~250ms; the end counts as reached within this window, and past it we treat
// the position as a seek and let go.
const END_WINDOW_SECONDS = 2;
// A position this far before the start is a seek back out of the clip.
const START_SLACK_SECONDS = 1;

export default class extends Controller {
  static targets = ['bar', 'time'];
  static values = { itemId: String, itemType: String, start: Number, end: Number };

  connect() {
    this.armed = false;
    this.entered = false;
    this.boundTimeUpdate = this.timeUpdate.bind(this);
    this.boundArm = this.arm.bind(this);
    this.boundOtherArmed = this.otherArmed.bind(this);

    window.addEventListener('supercast-timeupdate', this.boundTimeUpdate);
    window.addEventListener('clip:armed', this.boundOtherArmed);
    // PlayButtonController#play stops propagation, so a click action on the wrapper would never fire; a
    // capture listener sees the click first.
    this.element.addEventListener('click', this.boundArm, true);
    this.seedFromPlaybackDb();
  }

  disconnect() {
    window.removeEventListener('supercast-timeupdate', this.boundTimeUpdate);
    window.removeEventListener('clip:armed', this.boundOtherArmed);
    this.element.removeEventListener('click', this.boundArm, true);
  }

  async seedFromPlaybackDb() {
    if (!window.playbackDb) return;

    try {
      const data = await window.playbackDb.getPlaybackData(this.itemTypeValue, this.itemIdValue);
      // The stored position is the whole episode's: only seed when it falls inside this clip, otherwise the
      // reader never played it and the bar stays empty.
      const position = data?.currentTime;
      const inside = position >= this.startValue && position <= this.endValue;
      if (inside && !this.updatedLive) this.render(position);
    } catch (_error) { /* no stored position: the bar starts empty */ }
  }

  // Only a click on this clip's play (or pause) button counts as "playing this clip".
  arm(event) {
    if (!event.target.closest('[data-controller~="play-button"]')) return;

    this.armed = true;
    this.entered = false;
    // Another clip of this episode must stop watching: this click is about to move the shared player, which
    // could read as that clip's end.
    window.dispatchEvent(new CustomEvent('clip:armed', {
      detail: { itemId: this.itemIdValue, itemType: this.itemTypeValue, source: this }
    }));
  }

  otherArmed(event) {
    const { itemId, itemType, source } = event.detail;
    if (source === this || itemId !== this.itemIdValue || itemType !== this.itemTypeValue) return;

    this.armed = false;
    this.entered = false;
  }

  timeUpdate(event) {
    const { itemId, itemType, currentTime, isPlaying } = event.detail;
    if (String(itemId) !== this.itemIdValue || itemType !== this.itemTypeValue) return;

    this.updatedLive = true;
    this.render(currentTime);
    if (this.armed) this.watchEnd(currentTime, isPlaying);
  }

  // The click that armed us fires before the player has moved to the clip's start, so the first few
  // positions are still wherever the episode was; only once playback has been seen inside the clip do the
  // end and a seek away mean anything.
  watchEnd(currentTime, isPlaying) {
    const inside = currentTime >= this.startValue - START_SLACK_SECONDS && currentTime < this.endValue;
    if (inside) {
      this.entered = true;
    } else if (this.entered) {
      this.armed = false;
      const reachedEnd = currentTime >= this.endValue && currentTime <= this.endValue + END_WINDOW_SECONDS;
      if (reachedEnd && isPlaying) this.pausePlayer();
    }
  }

  pausePlayer() {
    const element = document.querySelector('[data-audio-player]');
    const player = element && this.application.getControllerForElementAndIdentifier(element, 'media--audio-player');
    player?.pause();
  }

  render(currentTime) {
    const length = this.endValue - this.startValue;
    if (!(length > 0) || !Number.isFinite(currentTime)) return;

    const played = Math.min(Math.max(currentTime - this.startValue, 0), length);
    if (this.hasBarTarget) this.barTarget.style.width = `${(played / length) * 100}%`;
    if (this.hasTimeTarget) this.timeTarget.textContent = `${this.clock(played)} / ${this.clock(length)}`;
  }

  clock(seconds) {
    const total = Math.floor(seconds);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  }
}
